import * as React from 'react';
import { UserContext } from '../Context/UserContext';
import { CommissionStatementRepository } from '../../MedicareProAPI/Repositories/CommissionStatement/CommissionStatementRepository';
import {
  CommissionStatementMappingRepository,
  CommissionStatementMappingSchema,
} from '../../MedicareProAPI/Repositories/CommissionStatement/CommissionStatementMappingRepository';
import { CommissionStatementMapping } from '../../MedicareProAPI/Entities/CommissionStatement/CommissionStatementMapping';
import { Utilities } from '../../MedicareProAPI/Utilities/Utilities';
import StyledModal from '../Components/StyledModal';
import { CommissionStatementMappingForm } from '../Components/CommissionStatement/CommissionStatementMappingForm';
import type { User } from '../../MedicareProAPI/Entities/User/User';
import { DeleteResponse } from '../../MedicareProAPI/Repositories/CoreRepository';

export interface AgentCommissionStatementMappingsPanelProps {
  user: User;
}

export default function AgentCommissionStatementMappingsPanel(
  props: AgentCommissionStatementMappingsPanelProps
): React.JSX.Element {
  const userData = React.useContext(UserContext);
  const [mappings, setMappings] = React.useState<Array<CommissionStatementMapping>>(null);
  const [mappingsLoading, setMappingsLoading] = React.useState<boolean>(true);

  const [mappingModal, setMappingModal] = React.useState<boolean>(false);

  const [formMapping, setFormMapping] = React.useState<CommissionStatementMapping>();

  const mappingRepository = new CommissionStatementMappingRepository();
  mappingRepository.setAccessToken(userData.userAccessToken);

  const loadMappings = (
    selectFirstStatement: boolean = false
  ): Promise<Array<CommissionStatementMapping>> => {
    setMappingsLoading(true);

    return mappingRepository
      .getCommissionStatementMappings(props.user.getUserId())
      .then((mappingCollection) => {
        const newMappings = mappingCollection.getItems();
        setMappings(newMappings);
        setMappingsLoading(false);

        if (selectFirstStatement && mappingCollection.getTotalItems() > 0) {
          setFormMapping(mappingCollection.getItem(0));
        }

        return newMappings;
      });
  };

  const createNewMapping = () => {
    const newMapping = new CommissionStatementMapping();
    newMapping.setUser(props.user);

    setFormMapping(newMapping);
    setMappingModal(true);
  };

  const handleMappingSave = (mapping: CommissionStatementMapping): void => {
    loadMappings();
  };

  const handleMappingDelete = (): void => {
    loadMappings(true);
  };

  React.useEffect(() => {
    loadMappings(true);
  }, []);

  return (
    <>
      <StyledModal
        size="lg"
        title={formMapping && formMapping.getId() ? 'Create Mapping' : 'Update Mapping'}
        footerCloseButton={true}
        show={mappingModal}
        onClose={(e) => setMappingModal(false)}
      >
        <CommissionStatementMappingForm
          commissionStatementMapping={formMapping}
          user={props.user}
          onSave={handleMappingSave}
          onDelete={handleMappingDelete}
        />
      </StyledModal>

      <button onClick={(e) => createNewMapping()} className="btn btn-success btn-sm mb-2">
        <i className="far fa-plus-circle mr-1"></i> Create Mapping
      </button>

      <div className="card pb-0 pt-0" style={{ height: 'auto' }}>
        <div className="row">
          {mappingsLoading === true && (!mappings || mappings.length === 0) && (
            <div className="col-12">
              <p className="alert alert-info small m-2">
                Loading... <i className="fas fa-spinner fa-spin"></i>
              </p>
            </div>
          )}
          {mappingsLoading === false && (!mappings || mappings.length === 0) && (
            <div className="col-12">
              <p className="alert alert-info small m-2">No mappings to display</p>
            </div>
          )}
          {mappings && mappings.length > 0 && (
            <>
              <div className="col-12 col-lg-2 col-xl-2 pt-2">
                {mappings &&
                  mappings.map((mapping) => {
                    const classList = [
                      'small',
                      'card',
                      'p-2',
                      'mb-2',
                      'cp',
                      'card-tall',
                      'border',
                      'border-info',
                      'ml-2',
                    ];

                    if (formMapping && mapping.getId() === formMapping.getId()) {
                      classList.push('bg-info');
                      classList.push('text-white');
                    }

                    return (
                      <div onClick={(e) => setFormMapping(mapping)} className={classList.join(' ')}>
                        <div>
                          <p className="mb-0">
                            <strong>
                              {mapping.getCommissionStatementMappingCompanies().getTotal() > 0
                                ? mapping
                                    .getCommissionStatementMappingCompanies()
                                    .getItems()
                                    .map((company) => company.getCompany().getCompanyName())
                                : mapping.getProviderName()}
                            </strong>
                          </p>
                        </div>
                      </div>
                    );
                  })}
              </div>

              <div className="col-12 col-lg-10 col-xl-10 pt-2">
                <div className="row pr-2">
                  <div className="col-12">
                    <CommissionStatementMappingForm
                      commissionStatementMapping={formMapping}
                      user={props.user}
                      onSave={handleMappingSave}
                      onDelete={handleMappingDelete}
                    />
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
